/**
 * Platform utility functions and icons for channel components.
 */

import type { Platform } from "@/lib/queries/channel";
import type { ComponentType } from "react";
import {
  FacebookIcon,
  InstagramIcon,
  LinkedInIcon,
  PinterestIcon,
  TikTokIcon,
  TwitchIcon,
  XIcon,
  YouTubeIcon,
  type SvgProps,
} from "../icons/social-icons";

/**
 * Get the icon component for a platform.
 */
type IconComponent = ComponentType<SvgProps>;

export function getPlatformIcon(platform: Platform): IconComponent {
  switch (platform) {
    case "youtube":
      return YouTubeIcon;
    case "tiktok":
      return TikTokIcon;
    case "instagram":
      return InstagramIcon;
    case "twitter":
      return XIcon;
    case "facebook":
      return FacebookIcon;
    case "twitch":
      return TwitchIcon;
    case "linkedin":
      return LinkedInIcon;
    case "pinterest":
      return PinterestIcon;
    default:
      return PinterestIcon;
  }
}

/**
 * Get the display label for a platform.
 */
export function getPlatformLabel(platform: Platform): string {
  switch (platform) {
    case "youtube":
      return "YouTube";
    case "tiktok":
      return "TikTok";
    case "instagram":
      return "Instagram";
    case "twitter":
      return "X (Twitter)";
    case "facebook":
      return "Facebook";
    case "twitch":
      return "Twitch";
    case "linkedin":
      return "LinkedIn";
    case "pinterest":
      return "Pinterest";
    default:
      return platform;
  }
}

/**
 * Get the brand color for a platform.
 */
export function getPlatformColor(platform: Platform): string {
  switch (platform) {
    case "youtube":
      return "#FF0000";
    case "tiktok":
      return "#000000";
    case "instagram":
      return "#E4405F";
    case "twitter":
      return "#000000";
    case "facebook":
      return "#1877F2";
    case "twitch":
      return "#9146FF";
    case "linkedin":
      return "#0A66C2";
    case "pinterest":
      return "#E60023";
    default:
      return "#666666";
  }
}

/**
 * Platform configuration for connect dialog.
 */
export interface PlatformConfig {
  platform: Platform;
  label: string;
  icon: IconComponent;
  description: string;
  supportsBusiness?: boolean;
  enabled?: boolean;
}

const ALL_PLATFORM_CONFIGS: PlatformConfig[] = [
  {
    platform: "instagram",
    label: "Instagram",
    icon: InstagramIcon,
    description: "Connect your Instagram account",
    enabled: true,
  },
  {
    platform: "tiktok",
    label: "TikTok",
    icon: TikTokIcon,
    description: "Connect your TikTok account",
    enabled: true,
  },
  {
    platform: "youtube",
    label: "YouTube",
    icon: YouTubeIcon,
    description: "Connect your YouTube account",
    enabled: true,
  },
  {
    platform: "twitter",
    label: "X (Twitter)",
    icon: XIcon,
    description: "Connect your X account",
    enabled: false,
  },
  {
    platform: "facebook",
    label: "Facebook",
    icon: FacebookIcon,
    description: "Connect your Facebook page",
    enabled: false,
  },
  {
    platform: "twitch",
    label: "Twitch",
    icon: TwitchIcon,
    description: "Connect your Twitch account",
    enabled: false,
  },
  {
    platform: "linkedin",
    label: "LinkedIn",
    icon: LinkedInIcon,
    description: "Connect your LinkedIn profile",
    enabled: false,
  },
  {
    platform: "pinterest",
    label: "Pinterest",
    icon: PinterestIcon,
    description: "Connect your Pinterest account",
    enabled: true,
  },
];

// Only export enabled platforms
export const PLATFORM_CONFIGS = ALL_PLATFORM_CONFIGS.filter((p) => p.enabled);
